import { useEffect, useMemo, useState } from 'react';
import type { AttributeDto, CategoryTreeNode, ProductSummaryDto } from '@ecom/contracts';
import { useAttributesStore } from '@/stores/attributes.store';
import { useAuthStore } from '@/stores/auth.store';
import { useCatalogStore } from '@/stores/catalog.store';
import { AsyncSelect } from '@/components/async-select';
import { categoryRows } from '@/lib/category-tree';
import { count } from '@/lib/format';
import { categoryOptions, sizeChartOptions } from '@/lib/options';
import { mediaUrl } from '@/lib/media';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { BilingualField, TextField, type BilingualValue } from '@/components/fields';
import { BoxIcon, ChevronDownIcon, GripIcon, PlusIcon, SearchIcon, SlidersIcon, TrashIcon } from '@/components/icons';
import { ListCard, ListToolbar, Page, Sheet, TableScroll, Thumb } from '@/components/page';
import { SortableGrid, SortableItem } from '@/components/sortable';
import {
  Alert,
  Button,
  ConfirmDialog,
  EmptyState,
  IconButton,
  Input,
  Pill,
  Switch,
  TableSkeleton,
  cx,
} from '@/components/ui';
import { SpecificationPickerSheet, attributeName } from './products/specifications';
import { SeoPanel, emptySeo, seoBody, type SeoDraft } from '@/components/seo-panel';
import { useSearchParams } from 'react-router-dom';

/**
 * Categories.
 *
 * The tree is the screen: nesting is what a shopper navigates, and a flat list
 * hides the one mistake that matters — a category filed under the wrong parent.
 * Deactivated, never deleted: products point at them and orders point at those
 * products.
 *
 * Creating, editing, ordering a category's products and choosing the
 * specifications it suggests open in a side panel, so the tree stays in view
 * beside the one being changed.
 *
 * The tree arrives whole — the admin's tree endpoint is not paged — so the
 * search narrows what is already here instead of asking the server again.
 */
export function CategoriesPage() {
  const {
    categories,
    categoriesLoaded,
    categoriesError,
    saving,
    error,
    loadCategories,
    createCategory,
    updateCategory,
    deactivateCategory,
    categoryOrder,
    openCategoryOrder,
    saveCategoryOrder,
    closeCategoryOrder,
  } = useCatalogStore();
  const loadTemplate = useAttributesStore((state) => state.loadTemplate);
  const closeTemplate = useAttributesStore((state) => state.closeTemplate);
  const locale = useUiStore((state) => state.locale);
  const t = useT();

  const [editing, setEditing] = useState<CategoryTreeNode | null>(null);
  const [creating, setCreating] = useState(false);
  const [deactivating, setDeactivating] = useState<CategoryTreeNode | null>(null);
  const [templateOf, setTemplateOf] = useState<CategoryTreeNode | null>(null);
  const [search, setSearch] = useState('');
  // Showing a switched-off category again is one click, not a form: the row says which.
  const [reactivating, setReactivating] = useState<string | null>(null);

  useEffect(() => {
    void loadCategories();
  }, [loadCategories, locale]);

  const allRows = useMemo(() => categoryRows(categories), [categories]);

  // `?edit=<id>` opens that category — where SEO health's "Fix" leads (ADR-095).
  const [params, setParams] = useSearchParams();
  const wanted = params.get('edit');
  useEffect(() => {
    if (!wanted || !categoriesLoaded) return;
    const found = findCategory(categories, wanted);
    if (found) setEditing(found);
    setParams((current) => {
      current.delete('edit');
      return current;
    }, { replace: true });
  }, [wanted, categoriesLoaded, categories, setParams]);
  const rows = useMemo(() => categoryRows(categories, search), [categories, search]);

  return (
    <Page
      title={t('categories')}
      subtitle={t('categoriesSubtitle')}
      actions={
        <Button onClick={() => setCreating(true)}>
          <PlusIcon className="size-4" />
          {t('newCategory')}
        </Button>
      }
      fill
    >
      <ListCard>
        {allRows.length > 0 ? (
          <ListToolbar>
            <div className="relative">
              <span className="pointer-events-none absolute inset-y-0 start-3 flex items-center text-ink-faint">
                <SearchIcon className="size-4" />
              </span>
              <Input
                type="search"
                value={search}
                placeholder={t('categorySearch')}
                aria-label={t('categorySearch')}
                onChange={(event) => setSearch(event.target.value)}
                className="ps-9"
              />
            </div>
          </ListToolbar>
        ) : null}

        {categoriesError ? (
          <div className="px-4 pt-4">
            <Alert>
              <span className="flex flex-wrap items-center justify-between gap-3">
                {categoriesError}
                <Button variant="secondary" size="sm" onClick={() => void loadCategories()}>
                  {t('tryAgain')}
                </Button>
              </span>
            </Alert>
          </div>
        ) : null}

        {error ? (
          <div className="px-4 pt-4">
            <Alert>{error}</Alert>
          </div>
        ) : null}

        {!categoriesLoaded ? (
          // Loading is not empty: "no categories yet" over a tree still on its
          // way invites someone to create the ones that exist.
          categoriesError ? null : <TableSkeleton rows={6} columns={3} />
        ) : allRows.length === 0 ? (
          <EmptyState title={t('noCategories')} body={t('noCategoriesHint')} icon={<BoxIcon />} />
        ) : rows.length === 0 ? (
          <EmptyState
            title={t('noResults')}
            body={t('categoryNoMatch', { search: search.trim() })}
            action={
              <Button variant="secondary" onClick={() => setSearch('')}>
                {t('clearFilters')}
              </Button>
            }
          />
        ) : (
          <TableScroll>
            <ul className="flex flex-col">
              {rows.map(({ node, depth, matched }) => (
                <li
                  key={node.id}
                  className="flex flex-wrap items-center gap-3 border-b border-line px-5 py-3 transition-colors last:border-0 hover:bg-surface-sunken/70"
                >
                  {/* Depth as indentation: the shape of the tree at a glance. */}
                  <span className="min-w-0 flex-1" style={{ paddingInlineStart: `${depth * 20}px` }}>
                    <span
                      className={cx(
                        'block text-sm font-medium',
                        // A parent shown only as the way to a match reads quieter than the match.
                        node.isActive && matched ? 'text-ink' : node.isActive ? 'text-ink-muted' : 'text-ink-faint',
                      )}
                    >
                      {node.name}
                    </span>
                    <span dir="ltr" className="block text-xs text-ink-faint">
                      {node.slug}
                    </span>
                  </span>

                  {node.productCount !== undefined ? (
                    <span className="text-xs whitespace-nowrap text-ink-muted" title={t('categoryProductCountHint')}>
                      {t('categoryProductCount', { count: count(node.productCount, locale) })}
                    </span>
                  ) : null}

                  {!node.isActive ? <Pill tone="neutral">{t('inactive')}</Pill> : null}

                  <span className="flex flex-wrap gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setTemplateOf(node);
                        void loadTemplate(node.id, node.parentId);
                      }}
                    >
                      <SlidersIcon className="size-4" />
                      {t('specifications')}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => void openCategoryOrder(node.id)}>
                      {t('orderProducts')}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setEditing(node)}>
                      {t('editContent')}
                    </Button>
                    {node.isActive ? (
                      <Button variant="ghost" size="sm" onClick={() => setDeactivating(node)}>
                        {t('deactivate')}
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={saving}
                        onClick={async () => {
                          setReactivating(node.id);
                          await updateCategory(node.id, { isActive: true });
                          setReactivating(null);
                        }}
                      >
                        {reactivating === node.id ? t('saving') : t('categoryShowAgain')}
                      </Button>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </TableScroll>
        )}
      </ListCard>

      {creating ? (
        <CategorySheet
          title={t('newCategory')}
          categories={categories}
          busy={saving}
          error={error}
          onClose={() => setCreating(false)}
          onSave={async (payload) => {
            if (await createCategory(payload)) setCreating(false);
          }}
        />
      ) : null}

      {editing ? (
        <CategorySheet
          title={t('editCategory')}
          categories={categories}
          category={editing}
          busy={saving}
          error={error}
          onClose={() => setEditing(null)}
          onSave={async (payload) => {
            if (await updateCategory(editing.id, payload)) setEditing(null);
          }}
        />
      ) : null}

      {categoryOrder ? (
        <CategoryOrderSheet
          key={categoryOrder.categoryId + categoryOrder.items.map((item) => item.id).join()}
          title={`${t('orderProducts')} — ${allRows.find((row) => row.node.id === categoryOrder.categoryId)?.node.name ?? ''}`}
          items={categoryOrder.items}
          busy={saving}
          error={error}
          onClose={closeCategoryOrder}
          onSave={async (ids) => {
            if (await saveCategoryOrder(categoryOrder.categoryId, ids)) closeCategoryOrder();
          }}
        />
      ) : null}

      {templateOf ? (
        <CategoryTemplateSheet
          key={templateOf.id}
          category={templateOf}
          onClose={() => {
            setTemplateOf(null);
            closeTemplate();
          }}
        />
      ) : null}

      <ConfirmDialog
        open={deactivating !== null}
        title={t('deactivateCategoryTitle')}
        body={t('deactivateCategoryBody')}
        confirmLabel={t('deactivate')}
        cancelLabel={t('cancel')}
        busy={saving}
        onConfirm={async () => {
          if (deactivating) await deactivateCategory(deactivating.id);
          setDeactivating(null);
        }}
        onCancel={() => setDeactivating(null)}
      />
    </Page>
  );
}

function CategorySheet({
  title,
  categories,
  category,
  busy,
  error,
  onClose,
  onSave,
}: {
  title: string;
  categories: CategoryTreeNode[];
  category?: CategoryTreeNode;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (payload: Record<string, unknown>) => void;
}) {
  const locale = useUiStore((state) => state.locale);
  const t = useT();

  // Both languages as stored. Filled from the resolved name, one box always
  // started empty — and saving then wrote that language over with the other,
  // or dropped it (R0).
  const [name, setName] = useState<BilingualValue>({
    ar: category?.content?.name.ar ?? (category && locale === 'ar' ? category.name : ''),
    en: category?.content?.name.en ?? (category && locale === 'en' ? category.name : ''),
  });
  const [slug, setSlug] = useState(category?.slug ?? '');
  const [parentId, setParentId] = useState(category?.parentId ?? '');
  const [sizeChartId, setSizeChartId] = useState(category?.sizeChartId ?? '');
  // The paragraph heading its page, and its search result (ADR-095).
  const [description, setDescription] = useState<BilingualValue>({
    ar: category?.content?.description.ar ?? '',
    en: category?.content?.description.en ?? '',
  });
  const [seo, setSeo] = useState<SeoDraft>(category?.content?.seo ?? emptySeo());
  // Google's category for the product feeds (ADR-096); its children inherit it.
  const [googleCategory, setGoogleCategory] = useState(category?.content?.googleCategory ?? '');

  /**
   * Parents are searched through the API like every other dropdown, with one
   * local rule layered on: a category cannot be re-parented under itself or its
   * own descendants. The API refuses either, and offering them would just
   * invite the error — the tree needed to know that is already on this screen.
   */
  const parentPage = async (search: string, cursor?: string) => {
    const page = await categoryOptions.page(search, cursor);
    return {
      ...page,
      options: page.options.filter(
        (option) => !category || !isSelfOrDescendant(categories, category.id, option.id),
      ),
    };
  };

  return (
    <Sheet
      open
      size="lg"
      title={title}
      subtitle={category?.name}
      onClose={onClose}
      footer={
        <>
          {error ? (
            <span className="me-auto">
              <Alert>{error}</Alert>
            </span>
          ) : null}
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            {t('cancel')}
          </Button>
          <Button
            disabled={busy || (!category && !name.en.trim())}
            onClick={() =>
              onSave({
                // English is required by the API: it is the fallback every other
                // locale falls back to.
                translations: {
                  en: { name: name.en.trim() || name.ar.trim(), description: description.en.trim() || null },
                  ...(name.ar.trim() ? { ar: { name: name.ar.trim(), description: description.ar.trim() || null } } : {}),
                },
                ...(slug.trim() && !category ? { slug: slug.trim() } : {}),
                parentId: parentId || null,
                sizeChartId: sizeChartId || null,
                seo: seoBody(seo),
                googleCategory: googleCategory.trim() || null,
              })
            }
          >
            {busy ? t('saving') : t('save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <BilingualField label={t('categoryName')} value={name} onChange={setName} />
        <BilingualField
          label={t('categoryDescription')}
          hint={t('categoryDescriptionHint')}
          multiline
          rows={3}
          limit={2000}
          value={description}
          onChange={setDescription}
        />

        {!category ? (
          <TextField
            label={t('slug')}
            hint={t('slugHint')}
            latin
            value={slug}
            onChange={setSlug}
          />
        ) : null}

        <div className="flex flex-col gap-1.5">
          <label htmlFor="parent" className="text-sm font-medium text-ink">
            {t('parentCategory')}
          </label>
          <AsyncSelect
            id="parent"
            value={parentId}
            emptyLabel={t('topLevel')}
            fetchPage={parentPage}
            fetchOne={categoryOptions.one}
            onChange={(id) => setParentId(id)}
          />
        </div>

        {/* Every product beneath shows it, unless one carries its own (ADR-072). */}
        <div className="flex flex-col gap-1.5">
          <label htmlFor="category-size-chart" className="text-sm font-medium text-ink">
            {t('sizeChart')}
          </label>
          <AsyncSelect
            id="category-size-chart"
            value={sizeChartId}
            emptyLabel={t('noSizeChart')}
            fetchPage={sizeChartOptions.page}
            fetchOne={sizeChartOptions.one}
            onChange={(id) => setSizeChartId(id)}
          />
          <p className="text-xs text-ink-muted">{t('categorySizeChartHint')}</p>
        </div>

        <TextField
          label={t('googleCategory')}
          hint={t('googleCategoryHint')}
          latin
          value={googleCategory}
          placeholder="6551"
          onChange={setGoogleCategory}
        />

        <SeoPanel
          kind="category"
          path={`/categories/${category?.slug ?? (slug.trim() || '…')}`}
          draft={seo}
          onChange={setSeo}
          names={name}
          ownWords={description}
          values={{ count: category?.productCount !== undefined ? String(category.productCount) : undefined }}
          hasImage={Boolean(category?.imageUrl)}
        />
      </div>
    </Sheet>
  );
}

function isSelfOrDescendant(nodes: CategoryTreeNode[], rootId: string, candidateId: string): boolean {
  if (rootId === candidateId) return true;

  const find = (list: CategoryTreeNode[]): CategoryTreeNode | undefined => {
    for (const node of list) {
      if (node.id === rootId) return node;
      const found = find(node.children);
      if (found) return found;
    }
    return undefined;
  };

  const root = find(nodes);
  if (!root) return false;

  const walk = (list: CategoryTreeNode[]): boolean =>
    list.some((node) => node.id === candidateId || walk(node.children));

  return walk(root.children);
}

/**
 * A category's hand-picked order (R3): what a shopper sees first when they
 * sort by "featured". Arrow buttons rather than dragging alone — a keyboard
 * and a phone have to be able to do this too.
 */
function CategoryOrderSheet({
  title,
  items,
  busy,
  error,
  onClose,
  onSave,
}: {
  title: string;
  items: ProductSummaryDto[];
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (productIds: string[]) => void;
}) {
  const t = useT();
  const [order, setOrder] = useState(items);

  const move = (index: number, by: -1 | 1) =>
    setOrder((current) => {
      const target = index + by;
      if (target < 0 || target >= current.length) return current;
      const next = [...current];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });

  return (
    <Sheet
      open
      title={title}
      subtitle={t('orderProductsHint')}
      onClose={onClose}
      footer={
        <>
          {error ? (
            <span className="me-auto">
              <Alert>{error}</Alert>
            </span>
          ) : null}
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            {t('cancel')}
          </Button>
          <Button disabled={busy || order.length === 0} onClick={() => onSave(order.map((item) => item.id))}>
            {busy ? t('saving') : t('saveOrder')}
          </Button>
        </>
      }
    >
      {order.length === 0 ? (
        <p className="py-6 text-center text-sm text-ink-muted">{t('noProductsListed')}</p>
      ) : (
        <ol className="flex flex-col divide-y divide-line rounded-control border border-line">
          {order.map((item, index) => (
            <li key={item.id} className="flex items-center gap-3 px-3 py-2">
              <span data-numeric className="w-6 text-center text-xs text-ink-faint">
                {index + 1}
              </span>
              <Thumb src={mediaUrl(item.imageUrl)} size="xs" />
              <span className="min-w-0 flex-1 truncate text-sm text-ink">{item.name}</span>
              {item.status !== 'ACTIVE' ? <Pill tone="neutral">{t(`status${item.status}`)}</Pill> : null}
              <IconButton
                label={t('moveUp')}
                onClick={() => move(index, -1)}
                disabled={index === 0}
                className="size-8 disabled:opacity-40"
              >
                <ChevronDownIcon className="size-4 rotate-180" />
              </IconButton>
              <IconButton
                label={t('moveDown')}
                onClick={() => move(index, 1)}
                disabled={index === order.length - 1}
                className="size-8 disabled:opacity-40"
              >
                <ChevronDownIcon className="size-4" />
              </IconButton>
            </li>
          ))}
        </ol>
      )}
    </Sheet>
  );
}

/** One row of a template being edited; `id` is the attribute's, which the sortable list keys on. */
interface TemplateRow {
  id: string;
  isRequired: boolean;
}

/**
 * The specifications a category suggests to its products (ADR-053): what it
 * asks itself, in the order it asks, each required or not — and, above them,
 * what its parent categories already ask, which are edited where they are
 * declared. A product can still add others of its own; this is the default,
 * not a fence.
 *
 * Saved as a whole, like the API takes it: the order is the list's order.
 * Owner work, since it shapes every product form in the category; anyone else
 * sees it read-only.
 */
function CategoryTemplateSheet({ category, onClose }: { category: CategoryTreeNode; onClose: () => void }) {
  const { library, template, templateLoading, saving, error, load, setForCategory } = useAttributesStore();
  const isOwner = useAuthStore((state) => state.user?.role === 'OWNER');
  const locale = useUiStore((state) => state.locale);
  const t = useT();

  const [rows, setRows] = useState<TemplateRow[]>([]);
  const [adding, setAdding] = useState(false);

  // The library names each row and is what the picker offers.
  useEffect(() => {
    void load();
  }, [load]);

  const loaded = template?.categoryId === category.id ? template : null;

  // The template arrived: the list starts from it. Adjusted while rendering.
  const [baseline, setBaseline] = useState<AttributeDto[] | null>(null);
  if (loaded && baseline !== loaded.own) {
    setBaseline(loaded.own);
    setRows(loaded.own.map((attribute) => ({ id: attribute.id, isRequired: attribute.isRequired ?? false })));
  }

  const libraryById = useMemo(() => new Map(library.map((attribute) => [attribute.id, attribute])), [library]);
  const infoOf = (id: string) => libraryById.get(id) ?? loaded?.own.find((attribute) => attribute.id === id);

  const rowIds = new Set(rows.map((row) => row.id));
  // Declared here as well as above: it shows once, here, where it can be edited.
  const inherited = (loaded?.fromParents ?? []).filter((attribute) => !rowIds.has(attribute.id));
  const inheritedIds = new Set(inherited.map((attribute) => attribute.id));
  const candidates = library.filter(
    (attribute) => attribute.isActive && !rowIds.has(attribute.id) && !inheritedIds.has(attribute.id),
  );

  const dirty =
    loaded !== null &&
    JSON.stringify(rows) !==
      JSON.stringify(loaded.own.map((attribute) => ({ id: attribute.id, isRequired: attribute.isRequired ?? false })));

  async function save() {
    const saved = await setForCategory(
      category.id,
      rows.map((row) => ({ attributeId: row.id, isRequired: row.isRequired })),
    );
    if (saved) onClose();
  }

  if (adding) {
    return (
      <SpecificationPickerSheet
        candidates={candidates}
        subtitle={t('templatePickerHint')}
        onAdd={(ids) => {
          setRows((current) => [...current, ...ids.map((id) => ({ id, isRequired: false }))]);
          setAdding(false);
        }}
        onClose={() => setAdding(false)}
      />
    );
  }

  return (
    <Sheet
      open
      size="lg"
      title={`${t('specifications')} — ${category.name}`}
      subtitle={t('templateHint')}
      onClose={onClose}
      footer={
        <>
          {error ? (
            <span className="me-auto">
              <Alert>{error}</Alert>
            </span>
          ) : null}
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            {isOwner ? t('cancel') : t('close')}
          </Button>
          {isOwner ? (
            <Button disabled={saving || !dirty} onClick={() => void save()}>
              {saving ? t('saving') : t('saveSpecifications')}
            </Button>
          ) : null}
        </>
      }
    >
      {!loaded ? (
        templateLoading ? (
          <TableSkeleton rows={4} columns={2} />
        ) : null
      ) : (
        <div className="flex flex-col gap-6">
          {!isOwner ? <Alert tone="info">{t('templateOwnerOnly')}</Alert> : null}

          {inherited.length > 0 ? (
            <section>
              <h3 className="text-sm font-medium text-ink">{t('templateInherited')}</h3>
              <p className="mt-0.5 mb-3 text-xs text-ink-muted">{t('templateInheritedHint')}</p>
              <ul className="flex flex-col divide-y divide-line rounded-control border border-line bg-surface-sunken/50">
                {inherited.map((attribute) => (
                  <li key={attribute.id} className="flex items-center gap-3 px-3 py-2.5">
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-ink">
                        {attributeName(libraryById.get(attribute.id) ?? attribute, locale)}
                      </span>
                      <span dir="ltr" className="block truncate text-xs text-ink-faint">
                        {attribute.code}
                        {attribute.unit ? ` · ${attribute.unit}` : ''}
                      </span>
                    </span>
                    {attribute.isRequired ? <Pill tone="warning">{t('required')}</Pill> : null}
                    <Pill tone="neutral">{t(`attrType${attribute.type}` as 'attrTypeTEXT')}</Pill>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          <section>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <h3 className="text-sm font-medium text-ink">{t('templateOwn')}</h3>
              {isOwner ? (
                <Button variant="secondary" size="sm" onClick={() => setAdding(true)}>
                  <PlusIcon className="size-4" />
                  {t('addSpecification')}
                </Button>
              ) : null}
            </div>

            {rows.length === 0 ? (
              <p className="rounded-control border border-dashed border-line px-3 py-6 text-center text-sm text-ink-muted">
                {t('templateEmpty')}
              </p>
            ) : (
              <SortableGrid items={rows} disabled={!isOwner} className="flex flex-col gap-2" onReorder={setRows}>
                {(row, index) => {
                  const info = infoOf(row.id);
                  const name = info ? attributeName(info, locale) : row.id;

                  return (
                    <SortableItem key={row.id} id={row.id}>
                      {({ attributes, listeners }) => (
                        <div className="flex items-center gap-3 rounded-control border border-line bg-surface px-2 py-2">
                          {isOwner ? (
                            <span
                              {...attributes}
                              {...listeners}
                              aria-label={t('dragToReorder')}
                              className="inline-flex size-8 shrink-0 cursor-grab touch-none items-center justify-center rounded-control text-ink-faint hover:bg-surface-sunken active:cursor-grabbing"
                            >
                              <GripIcon className="size-4" />
                            </span>
                          ) : null}
                          <span data-numeric className="w-5 shrink-0 text-center text-xs text-ink-faint">
                            {index + 1}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-medium text-ink">{name}</span>
                            <span dir="ltr" className="block truncate text-xs text-ink-faint">
                              {info?.code}
                              {info?.unit ? ` · ${info.unit}` : ''}
                            </span>
                          </span>
                          {/* Retired still sits in the list until someone takes it out;
                              products are no longer asked it either way. */}
                          {info && !info.isActive ? <Pill tone="neutral">{t('retired')}</Pill> : null}
                          {info ? (
                            <span className="hidden sm:inline-flex">
                              <Pill tone="neutral">{t(`attrType${info.type}` as 'attrTypeTEXT')}</Pill>
                            </span>
                          ) : null}
                          <span className="flex shrink-0 items-center gap-2 text-xs text-ink-muted">
                            <span aria-hidden className="hidden sm:inline">
                              {t('required')}
                            </span>
                            <Switch
                              label={`${t('required')} — ${name}`}
                              checked={row.isRequired}
                              disabled={!isOwner}
                              onChange={(isRequired) =>
                                setRows((current) =>
                                  current.map((entry) => (entry.id === row.id ? { ...entry, isRequired } : entry)),
                                )
                              }
                            />
                          </span>
                          {isOwner ? (
                            <IconButton
                              label={`${t('removeFromTemplate')} — ${name}`}
                              onClick={() => setRows((current) => current.filter((entry) => entry.id !== row.id))}
                              className="size-8 shrink-0 border-0 hover:text-danger"
                            >
                              <TrashIcon className="size-4" />
                            </IconButton>
                          ) : null}
                        </div>
                      )}
                    </SortableItem>
                  );
                }}
              </SortableGrid>
            )}
          </section>
        </div>
      )}
    </Sheet>
  );
}

/** A category anywhere in the tree. */
function findCategory(nodes: CategoryTreeNode[], id: string): CategoryTreeNode | undefined {
  for (const node of nodes) {
    if (node.id === id) return node;
    const below = findCategory(node.children, id);
    if (below) return below;
  }
  return undefined;
}
